/**@author Hieu Manh */
import { Block } from "@block";
import { spacing } from "@styles/spacing";
import { testProps } from "app/shared/util/test-props";
import React, { useMemo } from "react";
import { RegisterOptions, useController } from "react-hook-form";
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  StyleProp,
  StyleSheet,
  TextInputEndEditingEventData,
  TextInputKeyPressEventData,
  TextStyle,
} from "react-native";
import { Chip, HelperText, TextInput, useTheme } from "react-native-paper";
import { FormatMaskedTextInput, formatNumberThousand } from "../../../util/format-currency";

interface InputTextProps {
  control: any;
  name: string;
  inputProps?: object;
  style?: StyleProp<TextStyle>;
  label: string;
  onChangedText?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  keyboardType?: KeyboardTypeOptions;
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  onKeyPress?: (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  rightCurrency?: string;
  isShowZero?: boolean;
  /**
   * @description: Tắt tính năng gợi ý
   * */
  disabledSuggest?: boolean;
}

export const InputTextCurrency = (props: InputTextProps) => {
  const {
    control,
    name,
    inputProps,
    style = {},
    label,
    rules,
    onChangedText = () => {},
    onBlur = () => {},
    onFocus = () => {},
    keyboardType = "numeric",
    onKeyPress = () => {},
    rightCurrency,
    disabledSuggest = false,
    isShowZero = false,
  } = props;
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: "",
  });
  const formatMaskedTextInput = new FormatMaskedTextInput();
  const { fonts, colors } = useTheme();
  // Ngăn ngừa re-render khi bắt sự kiện onBlur;
  const [valueChanged, setValueChanged] = React.useState(false);
  const [showSuggest, toggleSuggest] = React.useState(false);
  const onChangedTextInput = (value: string) => {
    toggleSuggest(true);
    let rawAmount = "";
    if (value && value.length) {
      rawAmount = formatMaskedTextInput.getRawValue(value).toString();
    }
    value = rawAmount;
    if (value !== "NaN") {
      field.onChange(value);
      onChangedText(value);
      setValueChanged(true);
    }
  };

  const onBlurTextInput = () => {
    field.onBlur();
    if (valueChanged) {
      onBlur();
    }
    setTimeout(() => {
      toggleSuggest(false);
    }, 500);
  };

  const onFocusTextInput = () => {
    onFocus();
  };

  const onEndEditingTextInput = (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
    console.log("onEndEditingTextInput", e.nativeEvent.text);
  };

  const onKeyPressTextInput = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Tab") {
      e.preventDefault();
      onKeyPress(e);
    }
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        inputStyle: {
          flex: 1,
          color: colors.accent,
          backgroundColor: colors.backgroundForm,
          fontWeight: "700",
          textShadowColor: "black",
        },
        chipTextStyle: { color: colors.whiteText, fontFamily: fonts.regular.fontFamily },
        chipStyle: { backgroundColor: colors.accent },
        chipStyleCenter: { backgroundColor: colors.accent, marginHorizontal: spacing.small },
      }),
    [colors.accent, colors.backgroundForm, colors.whiteText, fonts],
  );

  const onPressSuggestMoney = (chipvalue: string) => {
    field.onChange(chipvalue);
    onChangedText(chipvalue);
    setValueChanged(true);
    setTimeout(() => {
      toggleSuggest(false);
    }, 100);
  };

  const createSuggestMoney = (
    <Block ml={spacing.medium} mt={spacing.small} display={showSuggest ? "flex" : "none"} row>
      <>
        {String(field.value).length === 1 && (
          <>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 4)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 4)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 5)).toString())}
              style={styles.chipStyleCenter}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 5)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 6)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 6)).toString())}
            </Chip>
          </>
        )}

        {String(field.value).length <= 3 && String(field.value).length >= 2 && (
          <>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 3)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 3)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 4)).toString())}
              style={styles.chipStyleCenter}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 4)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 5)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 5)).toString())}
            </Chip>
          </>
        )}

        {String(field.value).length >= 4 && String(field.value).length <= 6 && (
          <>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 1)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 1)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 2)).toString())}
              style={styles.chipStyleCenter}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 2)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 3)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 3)).toString())}
            </Chip>
          </>
        )}

        {String(field.value).length === 7 && String(field.value).length < 8 && (
          <>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 1)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 1)).toString())}
            </Chip>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 2)).toString())}
              style={styles.chipStyleCenter}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 2)).toString())}
            </Chip>
          </>
        )}
        {String(field.value).length === 8 && (
          <>
            <Chip
              onPress={() => onPressSuggestMoney((Number(field.value) * Math.pow(10, 1)).toString())}
              style={styles.chipStyle}
              textStyle={styles.chipTextStyle}>
              {formatNumberThousand((Number(field.value) * Math.pow(10, 1)).toString())}
            </Chip>
          </>
        )}
      </>
    </Block>
  );

  return (
    <>
      <Block row center>
        <TextInput
          mode={"flat"}
          outlineColor={"transparent"}
          theme={{
            colors: {
              primary: colors.accent,
              text: colors.accent,
            },
          }}
          style={[styles.inputStyle, style]}
          {...inputProps}
          keyboardType={keyboardType || "numeric"}
          ref={field.ref}
          onKeyPress={onKeyPressTextInput}
          value={formatMaskedTextInput.getValue(field.value, isShowZero)}
          onBlur={onBlurTextInput}
          onChangeText={onChangedTextInput}
          onEndEditing={onEndEditingTextInput}
          label={label}
          onFocus={onFocusTextInput}
          error={invalid}
          maxLength={14}
          right={rightCurrency?.length ? <TextInput.Affix textStyle={fonts.regular} text={rightCurrency} /> : null}
          {...testProps(name)}
        />
      </Block>
      {Number(field.value) > 0 && !disabledSuggest && createSuggestMoney}
      {invalid && error?.message && (
        <HelperText type="error" visible={invalid}>
          {error?.message}
        </HelperText>
      )}
    </>
  );
};
